import {createPortal} from 'react-dom';
import NameProposals from './NameProposals.jsx';
import ChatComposer from './ChatComposer.jsx';
import {ThreadAdminMenu,ThreadIssueLinks,ThreadLinkStatus} from './ThreadManagement.jsx';
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { Sparkles, ArrowLeft, ArrowUpRight, CornerUpLeft, MessageSquare, MoreHorizontal, X } from 'lucide-react';
import { indexDiscussion } from './discussionModel.js';
import MessageBody, { revealMessage } from './MessageBody.jsx';
const stamp = value => new Date(value).toLocaleString('zh-TW', {
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});
export default function ChatPanel({
  aiEnabled=false,
  aiHostRef,
  detailHost,
  onProposeVote,
  voteSources=[],
  issueLinkState,
  issueLinks=[],
  onManageThread,
  onOpenIssue,
  onViewVote,
  term,
  nameProposals,
  nameFocus,
  pollFocus,
  onBackToPoll,
  onBackToNames,
  visible:panelVisible,
  comments,
  commentsLoading=false,
  notices = [],
  onNew,
  chosenTab,
  user,
  canEdit,
  active,
  onSelect,
  onClose,
  onReturnToArticle=onClose,
  onQuote,
  onEditDiscussion,
  quoteStatus,
  onTarget,
  onDelete,
  onResolve,
  onRevision,
  expandedMessages,
  onExpanded,
  api,
  onRefresh,
  intent,
  onClearIntent,
  onLogin,
  following,
  onFollow,
  onRead,
  scrollPositions
}) {
  const canManageThreads=user?.role==='admin'&&Boolean(onManageThread);
  const model = useMemo(() => indexDiscussion(comments), [comments]);
  const root = model.rootOf(model.byId.get(active)),
    newThread = Boolean(intent?.type === 'new');
  const unavailable=Boolean(active&&!root&&!newThread);
  const tab = aiEnabled&&chosenTab==='ai'?'ai':root || newThread || unavailable ? 'threads' : chosenTab==='ai'?'chat':chosenTab,
    visible = panelVisible && tab!=='ai',
    listView = (tab === 'threads' && !root && !newThread && !unavailable) || tab === 'names',
    scope = root?.id || (unavailable ? active : newThread ? 'new-thread' : 'main');
  const detailActive = tab === 'threads' && Boolean(root || newThread || unavailable);
  const list = useRef(null), detailHeading = useRef(null), returnThread = useRef(null);
  const nameContext=tab==='names'?nameProposals.items.find(p=>p.current):root?.nameProposal?nameProposals.items.find(p=>p.id===root.nameProposal.id):null;
  const nameHeading=useRef(null),focusedName=useRef(null);
  useEffect(()=>{
    if(!visible||!nameFocus||nameContext?.id!==nameFocus.id||focusedName.current===nameFocus.sequence)return;
    const frame=requestAnimationFrame(()=>{nameHeading.current?.focus({preventScroll:true});focusedName.current=nameFocus.sequence;});
    return()=>cancelAnimationFrame(frame);
  },[visible,nameContext?.id,nameFocus?.sequence]);
  const pollContext=root?.pollContext;
  const pollHeading=useRef(null),pollQuestion=useRef(null);
  const [pollTitleClipped,setPollTitleClipped]=useState(false);
  useLayoutEffect(()=>{if(!visible||!pollContext)return;const title=pollHeading.current,question=pollQuestion.current;const measure=()=>setPollTitleClipped([title,question].some(el=>el&&el.scrollHeight>el.clientHeight+2));measure();const observer=new ResizeObserver(measure);if(title)observer.observe(title);if(question)observer.observe(question);return()=>observer.disconnect();},[visible,root?.id]);
  useEffect(()=>{if(visible&&pollContext&&pollFocus?.commentId===root.id){const frame=requestAnimationFrame(()=>pollHeading.current?.focus({preventScroll:true}));return()=>cancelAnimationFrame(frame);}},[visible,root?.id,pollFocus?.sequence]);
  const threadItems = useMemo(() => model.topics.map(item => ({
    ...item,
    unread: notices.find(n => n.rootId === item.root.id)?.unread || 0
  })), [model, notices]);
  const key = 'wiki-chat-compose:' + user?.id + ':' + term.id + ':' + scope;
  const [text, setText] = useState(''),
    [reply, setReply] = useState(null),
    [quote, setQuote] = useState(null),
    [editing, setEditing] = useState(null),
    [beforeEdit, setBeforeEdit] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [newReply, setNewReply] = useState(false);
  const view = useRef(),
    input = useRef(),
    nearBottom = useRef(true),
    previous = useRef({
      scope: null,
      count: 0
    }),
    live = useRef({
      scope,
      termId: term.id
    }),
    mounted = useRef(true),
    sending = useRef(false),
    draftRevision = useRef(0);
  live.current = {
    scope,
    termId: term.id,
    listView
  };
  const messages = root ? model.threads.get(root.id) || [] : model.chat;
  useEffect(() => () => {
    mounted.current = false;
  }, []);
  useLayoutEffect(() => {
    let draft;
    try {
      draft = JSON.parse(localStorage.getItem(key));
    } catch {}
    setText(draft?.text || '');
    setReply(draft?.reply || null);
    setQuote(draft?.quote || null);
    setEditing(draft?.editing || null);
    setBeforeEdit(draft?.beforeEdit || null);
    setError('');
    setNewReply(false);
  }, [key]);
  function update(values) {
    draftRevision.current++;
    const next = {
      text,
      reply,
      quote,
      editing,
      beforeEdit,
      ...values
    };
    setText(next.text);
    setReply(next.reply);
    setQuote(next.quote);
    setEditing(next.editing);
    setBeforeEdit(next.beforeEdit);
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      setError('草稿無法暫存，請勿關閉此頁。');
    }
  }
  useEffect(() => {
    if (!intent) return;
    if (intent.type === 'new') {
      let saved;
      try {
        saved = JSON.parse(localStorage.getItem(key));
      } catch {}
      update({
        text: intent.text || saved?.text || '',
        quote: intent.anchor,
        reply: null,
        editing: null
      });
    } else if (intent.type === 'edit-comment') {
      const c = comments.find(c => c.id === intent.parentId);
      if (c) editMessage(c);
    }
    input.current?.focus();
  }, [intent]);
  useLayoutEffect(() => {
    const el = list.current;
    if (!el) return;
    const scrollKey = 'chat:' + term.id + ':thread-list';
    if (tab === 'threads' && (!detailActive || window.innerWidth > 850)) el.scrollTop = scrollPositions.current[scrollKey] || 0;
    const remember = () => { if (el.clientHeight && el.getClientRects().length) scrollPositions.current[scrollKey] = el.scrollTop; };
    el.addEventListener('scroll', remember);
    return () => el.removeEventListener('scroll', remember);
  }, [term.id, tab, panelVisible, detailActive]);
  useLayoutEffect(() => {
    if (listView && tab === 'threads') return;
    const el = view.current,
      scrollKey = 'chat:' + term.id + ':' + (tab==='names'?'names':listView ? 'thread-list' : scope);
    if (!el) return;
    el.scrollTop = scrollPositions.current[scrollKey] ?? (listView ? 0 : el.scrollHeight);
    nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    const remember = () => {
      scrollPositions.current[scrollKey] = el.scrollTop;
      nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
      if (nearBottom.current) setNewReply(false);
    };
    el.addEventListener('scroll', remember);
    // Composer height and panel resizing must not clip the latest message controls.
    // Only follow the bottom when the reader was already there.
    const observer = new ResizeObserver(() => {
      if (nearBottom.current && !listView) el.scrollTop = el.scrollHeight;
    });
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', remember);
      observer.disconnect();
    };
  }, [scope, term.id, visible, listView, tab, detailHost]);
  useEffect(() => {
    if (visible && !listView && user && nearBottom.current) onRead?.();
  }, [visible, scope, messages.length, listView]);
  useLayoutEffect(() => {
    const before = previous.current;
    if (!listView && view.current && before.scope === scope && messages.length > before.count) {
      if (nearBottom.current) view.current.scrollTop = view.current.scrollHeight;else setNewReply(true);
    }
    previous.current = {
      scope,
      count: messages.length
    };
  }, [scope, messages.length]);
  function navigate(c) {
    if (c) returnThread.current = c.id;
    onClearIntent();
    onSelect(c, 'threads');
    requestAnimationFrame(() => {
      const target = c ? detailHeading.current : list.current?.querySelector('[data-thread-id="' + returnThread.current + '"]') || list.current;
      target?.focus({preventScroll:true});
    });
  }
  function switchTab(next) {
    onClearIntent();
    onSelect(null, next);
  }
  function chooseReply(c) {
    if (!root && !c.chat) {
      navigate(c);
      return;
    }
    update({
      ...(editing ? beforeEdit || {
        text: ''
      } : {}),
      reply: c.id,
      editing: null,
      beforeEdit: null,
      quote: null
    });
    input.current?.focus();
  }
  function editMessage(c) {
    if (sending.current) return;
    update({
      beforeEdit: editing ? beforeEdit : {
        text,
        reply,
        quote
      },
      editing: c,
      text: c.body,
      quote: null,
      reply: null
    });
    input.current?.focus();
  }
  function cancelEdit() {
    update({
      ...beforeEdit,
      editing: null,
      beforeEdit: null,
      ...(!beforeEdit ? {
        text: '',
        reply: null,
        quote: null
      } : {})
    });
    input.current?.focus();
  }
  useLayoutEffect(() => {
    const el = input.current;
    if (!el) return;
    const panel = el.closest('.chat-panel'), content = el.closest('.chat-standard-content'),
      form = el.form, discussionContext = content.querySelector('.chat-discussion-context'),
      notices = form.querySelector('.chat-compose-notices'), actions = form.querySelector('.chat-compose-actions');
    let disposed = false, frame, width = el.clientWidth;
    const pixels = value => parseFloat(value) || 0;
    const outerHeight = node => {
      const style = getComputedStyle(node);
      return node.getBoundingClientRect().height + pixels(style.marginTop) + pixels(style.marginBottom);
    };
    const resize = () => {
      if (disposed) return;
      // Mobile keyboards can reduce the visual viewport without resizing 100dvh.
      const viewport = window.visualViewport;
      panel.style.maxHeight = Math.max(0,
        (viewport ? viewport.height + viewport.offsetTop : innerHeight) - panel.getBoundingClientRect().top) + 'px';
      const style = getComputedStyle(el);
      const border = pixels(style.borderTopWidth) + pixels(style.borderBottomWidth);
      const min = Math.max(pixels(style.minHeight), Math.ceil(pixels(style.lineHeight) + pixels(style.paddingTop) + pixels(style.paddingBottom) + border));
      const formStyle = getComputedStyle(form), streamStyle = getComputedStyle(view.current);
      const actionHeight = formStyle.display === 'grid'
        ? (notices.scrollHeight ? pixels(formStyle.rowGap) : 0)
        : outerHeight(actions);
      const chrome = pixels(formStyle.paddingTop) + pixels(formStyle.paddingBottom) + pixels(formStyle.borderTopWidth) + pixels(formStyle.borderBottomWidth) + actionHeight;
      const streamPadding = pixels(streamStyle.paddingTop) + pixels(streamStyle.paddingBottom);
      // Context and errors share only the space left after a complete input line
      // and the action row. They scroll instead of squeezing the submit button.
      discussionContext.style.maxHeight = Math.max(0, Math.min(content.clientHeight * .35,
        content.clientHeight - chrome - min - streamPadding - Math.min(80, notices.scrollHeight))) + 'px';
      notices.style.maxHeight = Math.max(0, Math.min(80,
        content.clientHeight - outerHeight(discussionContext) - chrome - min - streamPadding)) + 'px';
      const available = content.clientHeight - outerHeight(discussionContext) - chrome - outerHeight(notices) - streamPadding;
      const max = Math.max(min, Math.min(pixels(style.maxHeight) || 120, available));
      el.style.overflowY = 'hidden';
      el.style.height = 'auto';
      const required = Math.ceil(el.scrollHeight + border);
      el.style.height = Math.min(max, Math.max(min, required)) + 'px';
      el.style.overflowY = required > el.clientHeight + border ? 'auto' : 'hidden';
    };
    const scheduleResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(resize);
    };
    resize();
    const observer = new ResizeObserver(() => {
      scheduleResize();
    });
    [content, discussionContext, notices, actions].forEach(node => observer.observe(node));
    const inputObserver = new ResizeObserver(() => {
      if (el.clientWidth !== width) { width = el.clientWidth; scheduleResize(); }
    });
    inputObserver.observe(el);
    window.addEventListener('resize', scheduleResize);
    window.visualViewport?.addEventListener('resize', scheduleResize);
    window.visualViewport?.addEventListener('scroll', scheduleResize);
    document.fonts?.ready.then(scheduleResize);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      inputObserver.disconnect();
      window.removeEventListener('resize', scheduleResize);
      window.visualViewport?.removeEventListener('resize', scheduleResize);
      window.visualViewport?.removeEventListener('scroll', scheduleResize);
      panel.style.removeProperty('max-height');
      discussionContext.style.removeProperty('max-height');
      notices.style.removeProperty('max-height');
    };
  }, [text, listView, visible, editing, user, error, reply, quote, root?.id, detailHost]);
  async function submit(e) {
    e.preventDefault();
    if (sending.current || !text.trim()) return;
    if (!user) {
      onLogin();
      return;
    }
    sending.current = true;
    setBusy(true);
    setError('');
    const sentScope = scope,
      sentKey = key,
      sentTerm = term.id,
      sentRevision = draftRevision.current;
    let storedAtSend;
    try {
      storedAtSend = localStorage.getItem(sentKey);
    } catch {}
    try {
      let result;
      const payload = {
        termId: term.id,
        body: text,
        parentId: root ? reply || root.id : null,
        ...(!root && !quote && !newThread ? {
          chat: true,
          ...(reply ? {
            replyTo: reply
          } : {})
        } : {}),
        ...(quote ? {
          anchor: quote
        } : newThread?{anchor:{field:'page',quote:term.label,version:term.version}}:{})
      };
      if (editing) result = await api('comments/' + editing.id, 'PATCH', {
        version: editing.version,
        body: text
      }); else result = await api('comments', 'POST', payload);
      await onRefresh();
      try {
        if (localStorage.getItem(sentKey) === storedAtSend) {
          if (editing) localStorage.setItem(sentKey, JSON.stringify({
            ...beforeEdit,
            editing: null,
            beforeEdit: null
          }));else localStorage.removeItem(sentKey);
        }
      } catch {}
      if (mounted.current && draftRevision.current === sentRevision && live.current.scope === sentScope && live.current.termId === sentTerm) {
        update(editing ? {
          ...beforeEdit,
          editing: null,
          beforeEdit: null,
          ...(!beforeEdit ? {
            text: ''
          } : {})
        } : {
          text: '',
          reply: null,
          quote: null,
          editing: null,
          beforeEdit: null
        });
        onClearIntent();
        if (!root && quote) {
          onSelect(result);
        }
        requestAnimationFrame(() => {
          if (!live.current.listView) {
            if (view.current) view.current.scrollTop = view.current.scrollHeight;
            revealMessage(result.id,false);
          }
        });
      }
    } catch (e) {
      if (mounted.current && live.current.scope === sentScope && live.current.termId === sentTerm) setError(e.message);
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  function menu(e, fn) {
    e.currentTarget.closest('details').open = false;
    fn();
  }
  const replyMessage = comments.find(c => c.id === reply),
    context = quote || root?.anchor;
  const conversationContent = <><header className="discussion-detail-heading" hidden={!detailActive}><button type="button" onClick={onReturnToArticle}><ArrowLeft size={17}/>返回{aiEnabled?'編輯':'文章'}</button><h2 ref={detailHeading} tabIndex={-1}>{term.label}</h2>{canManageThreads&&root&&<ThreadAdminMenu root={root} onManage={onManageThread}/>}<button type="button" className="icon-button" aria-label="關閉討論內容，返回清單" onClick={()=>navigate(null)}><X size={18}/></button></header> {unavailable&&<div className="thread-unavailable" role="status"><MessageSquare size={24} aria-hidden="true"/><p>{commentsLoading?'正在讀取討論串…':'此討論串已刪除或無法存取'}</p>{!commentsLoading&&<div><button type="button" onClick={()=>navigate(null)}>返回所有討論串</button><button type="button" onClick={onReturnToArticle}>返回{aiEnabled?'編輯':'文章'}</button></div>}</div>}<div className="chat-standard-content" hidden={unavailable||tab==='ai'||(tab==='threads'&&!detailActive)}>
 <div className="chat-discussion-context">
 {root&&<ThreadLinkStatus state={issueLinkState}/>}
 {root&&<ThreadIssueLinks canRestore={user?.role==='admin'} links={issueLinks.filter(link=>link.rootId===root.id)} onOpen={onOpenIssue}/>}
 {nameContext&&<div className="name-discussion-context"><button onClick={()=>onBackToNames(nameContext)}><ArrowLeft size={16}/>返回譯名</button><h2 ref={nameHeading} tabIndex={-1} className="name-discussion-heading">{nameContext.name}</h2></div>}
 {pollContext&&<div className="name-discussion-context poll-discussion-context"><button onClick={()=>onBackToPoll(pollContext)}><ArrowLeft size={16}/>返回投票</button><h2 ref={pollHeading} tabIndex={-1} className="name-discussion-heading">{pollContext.optionName||pollContext.question}</h2>{pollContext.optionName&&<p ref={pollQuestion}>{pollContext.question}</p>}{pollTitleClipped&&<button className="poll-full-context" onClick={()=>revealMessage(root.id)}>查看完整{pollContext.optionName?'選項':'問題'}</button>}<button onClick={()=>onRevision(pollContext.baseVersion,pollContext.baseVersion)}>依第 {pollContext.baseVersion} 版提出</button>{user&&onFollow&&<button onClick={onFollow}>{following?'已追蹤':'追蹤'}</button>}</div>}
 {!nameContext&&!pollContext&&(root || newThread) && <div className="chat-topic"><span>{root ? root.body.slice(0, 72) : '新增討論'}</span>{root && onFollow && <button onClick={onFollow}>{following ? '已追蹤' : '追蹤'}</button>}</div>}
 {root&&(root.review||canEdit)&&<div className="chat-review-context">{root.review&&<><span>第 {root.review.version} 版的回饋</span><button onClick={()=>onRevision(root.review.version,root.review.version)}>查看該版</button></>}{canEdit&&onEditDiscussion&&<button onClick={()=>onEditDiscussion(root)}>{root.review?'依此回饋編輯':'依此討論編輯'}</button>}</div>}
 {!root?.nameProposal && !listView && context && <div className="chat-quote"><button disabled={!root} onClick={() => onQuote(root)}><span>{context.field === 'english' ? '英文引用' : '原文'}</span><q>{context.quote}</q>{root && <ArrowUpRight size={15} />}</button>{root && quoteStatus?.(root) && <div className="quote-location-note"><span>{quoteStatus(root)}</span>{root.anchor?.field!=='english' && root.anchor?.version && <button onClick={()=>onRevision?.(root.anchor.version)}>查看引用版本</button>}</div>}{root?.anchor?.field === 'english' && <details><summary>來源與中文位置</summary><a href={root.anchor.sourceUrl} target="_blank" rel="noreferrer">英文來源 ↗</a><button onClick={() => onTarget(root)}>中文位置 ↗</button></details>}{quote && <button className="icon-button" aria-label="取消引用" onClick={() => {
        update({
          quote: null
        });
        requestAnimationFrame(()=>input.current?.focus({preventScroll:true}));
      }}><X size={16} /></button>}</div>}
 </div>
 <div id="chat-panel-content" role="tabpanel" aria-labelledby={'chat-tab-' + (tab==='names'?'threads':tab)} className="threads-view chat-stream" ref={view} aria-label={root ? '討論串訊息' : '聊天室訊息'}><div hidden={tab!=='names'}><NameProposals onSourceDiscussion={source=>navigate(model.byId.get(source.sourceCommentId)||model.byId.get(source.sourceRootId))} hideTitle={Boolean(nameContext)} showDiscussion focusedId={nameProposals.items.find(p=>p.current)?.id} visible={visible&&tab==='names'} term={term} user={user} canEdit={canEdit} api={api} data={nameProposals} onRefresh={onRefresh} onDiscuss={navigate} onLogin={onLogin} comments={comments}/></div>{root?.nameProposal&&<NameProposals onSourceDiscussion={source=>navigate(model.byId.get(source.sourceCommentId)||model.byId.get(source.sourceRootId))} hideTitle={Boolean(nameContext)} key={root.id} focusedId={root.nameProposal.id} visible={visible} term={term} user={user} canEdit={canEdit} api={api} data={nameProposals} onRefresh={onRefresh} onDiscuss={navigate} onLogin={onLogin} comments={comments}/ >}{tab==='names'?null:listView ? null : <>{newReply && <button className="new-message-jump" onClick={() => {
          view.current.scrollTop = view.current.scrollHeight;
          setNewReply(false);
          onRead?.();
        }}>有新訊息 ↓</button>}{!messages.length && !newThread && <div className="chat-empty"><MessageSquare size={24} /><p>從這裡開始討論「{term.label}」。</p></div>}
 {(newThread ? [] : messages).map(c => {
          const target = model.byId.get(c.replyTo || c.parentId),
            count = Math.max(0, (model.threads.get(c.id)?.length || 1) - 1);
          return <article id={'message-' + c.id} className="chat-message" key={c.id}><header><strong>{!(c.ai && c.author.name === 'AI') && c.author.name}{c.ai && <small className="chat-ai-badge" title={'由 ' + c.ai.requestedBy + ' 邀請'} aria-label={'AI，由 ' + c.ai.requestedBy + ' 邀請'}>AI</small>}</strong><div className="message-meta"><time>{stamp(c.createdAt)}</time><div className="chat-hover-actions" role="group" aria-label="訊息操作">{user && !c.deletedAt && <button className="chat-quick-action" aria-label={root ? '在串內回覆' : '在聊天回覆'} onClick={() => chooseReply(c)}><CornerUpLeft size={15} />回覆</button>}{!root && <button className="chat-quick-action" aria-label={count ? '查看討論串 · ' + count + ' 則回覆' : '另開討論串'} onClick={() => navigate(c)}><MessageSquare size={15} />討論串{count > 0 && <span>{count}</span>}</button>}<details className="message-menu"><summary aria-label={c.author.name + '的留言操作'}><MoreHorizontal size={18} /></summary><div className="message-menu-items">{!c.deletedAt && user && onProposeVote && <button onClick={e => menu(e, () => onProposeVote(c, model.rootOf(c)))}>提出投票</button>}{!c.deletedAt && user?.id === c.author.id && <button onClick={e => menu(e, () => editMessage(c))}>編輯留言</button>}{user?.canDeleteComments && <button onClick={e => menu(e, () => onDelete(c))}>{c.deletedAt ? '還原留言' : '刪除留言'}</button>}{!c.deletedAt && !c.parentId && canEdit && <button onClick={e => menu(e, () => onResolve(c))}>{c.resolved ? '重新討論' : '記錄結論'}</button>}<button className="chat-touch-action" onClick={e => menu(e, () => chooseReply(c))}>{root ? '在串內回覆' : '在聊天回覆'}</button>{!root && <button className="chat-touch-action" onClick={e => menu(e, () => navigate(c))}>{count ? '查看討論串' : '另開討論串'}</button>}</div></details></div></div></header>
 {target && (!root || target.id !== root.id) && <button className="chat-reply-context" onClick={() => revealMessage(target.id)}><CornerUpLeft size={13} />{target.author.name}：{target.body.slice(0, 64)}</button>}
 {!root && c.anchor && c.anchor.field !== 'page' && <button className="chat-anchor" onClick={() => onQuote(c)}><q>{c.anchor.quote}</q>{quoteStatus?.(c)?<span className="quote-location-note">原文已變更</span>:<ArrowUpRight size={14} />}</button>}
 {c.quoteChange&&<div className="discussion-edit-note"><span>引用段落已更新</span><button onClick={()=>onRevision(c.quoteChange.version,c.quoteChange.previous)}>查看差異</button></div>}
 {c.editLinks?.length>0&&<details className="discussion-edit-links"><summary>相關修改 · {c.editLinks.length}</summary>{c.editLinks.map(link=><button key={link.version} onClick={()=>onRevision(link.version,link.previous)}>第 {link.version} 版 · 查看差異</button>)}</details>}
 {voteSources.filter(source=>source.sourceCommentId===c.id&&!c.deletedAt).length>0&&<div className="message-vote-links">{voteSources.filter(source=>source.sourceCommentId===c.id&&!c.deletedAt).map(source=>source.targetDeleted?<span key={source.id}>投票項目已刪除</span>:<button key={source.id} onClick={()=>onViewVote?.(source)}>{source.target.kind==='names'?'查看譯名':source.target.optionId?'查看投票項目':'查看議題'}<ArrowUpRight size={14}/></button>)}</div>}
 <MessageBody comment={c} expanded={expandedMessages.has(c.id)} onExpanded={onExpanded} />{c.resolved && <blockquote className="chat-conclusion"><span>暫定結論：{c.conclusion}</span>{c.resolutionVersion&&<a href={'?term='+encodeURIComponent(term.id)+'&view=history&oldid='+(c.resolutionOldVersion??Math.max(1,c.resolutionVersion-1))+'&diff='+c.resolutionVersion} onClick={e=>{if(onRevision&&!e.metaKey&&!e.ctrlKey&&!e.shiftKey&&!e.altKey&&e.button===0){e.preventDefault();onRevision(c.resolutionVersion,c.resolutionOldVersion);}}}>查看第 {c.resolutionVersion} 版修改 ↗</a>}</blockquote>}
</article>;
        })}</>}</div>
 {!listView && <ChatComposer state={{
      text,
      editing,
      busy,
      error,
      replyMessage
    }} actions={{
      submit,
      cancelEdit,
      update,
      onLogin
    }} context={{
      input,
      user,
      canEdit,
      root,
      newThread
    }} />}</div></>;
  return <><div className="chat-mobile-context"><button onClick={onReturnToArticle} aria-label={'返回條目：' + term.label}><ArrowLeft size={16} /><span>{term.label}</span></button></div><header className="chat-heading"><div className={"chat-tabs"+(aiEnabled?" with-ai-tools":"")} role="tablist" aria-label="討論方式">{[['chat', '聊天'], ['threads', '討論串'],...(aiEnabled?[['ai','AI 輔助']]:[])].map(([value, label]) => <button type="button" key={value} id={'chat-tab-' + value} role="tab" aria-selected={(tab==='names'?'threads':tab) === value} aria-controls={value==='ai'?'chat-ai-content':value==='threads'?'discussion-thread-index':'chat-panel-content'} tabIndex={(tab==='names'?'threads':tab) === value ? 0 : -1} onClick={() => switchTab(value)} onKeyDown={e => {
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
            e.preventDefault();
            const tabs=['chat','threads',...(aiEnabled?['ai']:[])];
            const next=e.key==='Home'?tabs[0]:e.key==='End'?tabs.at(-1):tabs[(tabs.indexOf(tab==='names'?'threads':tab)+(e.key==='ArrowLeft'?-1:1)+tabs.length)%tabs.length];
            switchTab(next);
            document.getElementById('chat-tab-' + next)?.focus();
          }
        }}>{value==='ai'&&<Sparkles size={16} aria-hidden="true"/>}{label}{value === 'threads' && threadItems.length > 0 && <span>{threadItems.length} 串</span>}</button>)}</div><button className="icon-button" onClick={onClose} aria-label="關閉討論面板"><X size={19} /></button></header>
<div id="discussion-thread-index" role="tabpanel" aria-labelledby="chat-tab-threads" className="discussion-index chat-stream" tabIndex={-1} ref={list} hidden={tab!=='threads'} aria-label="討論串清單"><div className="chat-thread-list"><div className="chat-list-tools"><span>{threadItems.length} 串討論</span>{<div className="chat-list-management">{canManageThreads&&<button type="button" onClick={()=>onManageThread('deleted')}>已刪除</button>}{user&&<button onClick={onNew}>新增討論串</button>}</div>}</div><ThreadLinkStatus state={issueLinkState}/>{!threadItems.length && <p className="chat-empty">尚無討論串。可從聊天開串，或反白文章文字建立討論。</p>}{threadItems.map(item => <div className={"chat-thread-row"+(canManageThreads?" has-management":"")} key={item.root.id}><button className="chat-thread-item" data-thread-id={item.root.id} aria-current={root?.id===item.root.id?'true':undefined} aria-controls="discussion-detail-panel" aria-label={(item.root.deletedAt?'留言已刪除':item.root.body) + (item.unread ? '，' + item.unread + ' 則未讀' : '')} onClick={() => navigate(item.root)}><strong>{item.root.deletedAt?'留言已刪除':item.root.body}</strong><span className="thread-initiator">{item.root.author?.name||'未知成員'} 發起 · {stamp(item.root.createdAt)}</span>{item.root.anchor && item.root.anchor.field !== 'page' && <q>{item.root.deletedAt?'引用已隱藏':item.root.anchor.quote}</q>}<span>{item.root.chat ? '來自聊天' : item.root.anchor?.field === 'english' ? '英文選文' : item.root.anchor?.field === 'page' ? '條目討論' : '中文選文'} · {item.count} 則回覆{item.unread > 0 && <span className="thread-unread-dot" aria-hidden="true" />}{item.root.quoteChange&&<span className="quote-change-dot" role="img" aria-label="引用段落已更新" title="引用段落已更新"/>}{item.root.resolved ? ' · 已有結論' : ''}</span>{item.count>0?<time>{item.latestReply?.author?.name||'成員'} · {stamp(item.latest)} 回覆</time>:<time>尚無回覆</time>}</button>{canManageThreads&&<ThreadAdminMenu root={item.root} onManage={onManageThread} label={'討論串操作：'+item.root.body}/>}<ThreadIssueLinks canRestore={user?.role==='admin'} links={issueLinks.filter(link=>link.rootId===item.root.id)} onOpen={onOpenIssue}/></div>)}</div></div>{detailActive ? detailHost && createPortal(conversationContent, detailHost) : <div className="conversation-inline" hidden={tab==='threads'}>{conversationContent}</div>}<div id="chat-ai-content" className="chat-ai-content" ref={aiHostRef} role="tabpanel" aria-labelledby="chat-tab-ai" hidden={!aiEnabled||tab!=='ai'}/></>;
}
